import { Injectable } from '@angular/core';
import { PhoneNumber, PhoneNumberFormat, PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber';
import { CountryInfo, PhoneNumberInfo } from '../interfaces/country-info.interface';

@Injectable({ providedIn: 'root' })
export class PhoneNumberService {
  private phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();
  private phonesInfo: Map<string, PhoneNumberInfo> = new Map();

  /**
   * @description Gets the phone's number format code. Example: 1 for '12024561414'.
   * @param {string} phone Current phone number as string.
   * @returns {number}
   */
  public getCountryCode(phone: string): number {
    return this.convertedNumber(phone).getCountryCodeOrDefault();
  }

  /**
   * @description Gets the phone's country code. Example: 'US' for '1 202-456-1414'.
   * @param {string} phone Current phone number as string.
   * @return {string}
   */
  public getCountryISO(phone: string): string {
    return this.phoneUtil.getRegionCodeForNumber(this.convertedNumber(phone));
  }

  /**
   * @description Checks if phone number format is valid for selected country with ISO.
   * @param {string} phone Current phone number as string.
   * @param {string} iso ISO country code.
   * @returns {boolean}
   */
  public isPhoneNumberValidForISO(phone: string, iso: string): boolean {
    return this.phoneUtil.isValidNumberForRegion(this.convertedNumber(phone), iso);
  }

  /**
   * @description Checks if phone number format is valid.
   * @param {string} phone Current phone number as string.
   * @return {boolean}
   */
  public isPhoneNumberValid(phone: string): boolean {
    return this.phoneUtil.isValidNumber(this.convertedNumber(phone));
  }

  /**
   * @description Prints the phone's national number. Example: '202-456-1414' -> '2024561414'
   * @param {string} phone Current phone number as string.
   * @returns {string}
   */
  public getNationalNumber(phone: string): string {
    return this.convertedNumber(phone).getNationalNumber()?.toString() ?? '';
  }

  public getCountryPhoneNumberInfo(iso: string): PhoneNumberInfo {
    return this.phonesInfo.get(iso) || this.phonesInfo.get('US');
  }

  public getCountryPhoneCode(iso: string): string {
    return this.phonesInfo.get(iso)?.phoneCode ?? '';
  }

  /**
   * @description Sets up phone examples and codes for all countries.
   * @param {CountryInfo[]} countries Countries list.
   */
  public setupCountryPhoneInfo(countries: CountryInfo[]): void {
    countries.forEach((country: CountryInfo) => {
      const countryExampleMobileNumber: PhoneNumber = this.getPhoneExample(country.iso);

      country.isDisabled = !countryExampleMobileNumber;

      if (countryExampleMobileNumber) {
        const phoneCode = `+${countryExampleMobileNumber.getCountryCode()}`;
        const sampleE164Phone: string = this.phoneUtil
          .format(countryExampleMobileNumber, PhoneNumberFormat.E164)
          .substring(phoneCode.length);

        this.phonesInfo.set(country.iso, { phoneCode, sampleE164Phone });
      }
    });
  }

  /**
   * @description Gets mobile phone example for specified iso.
   * @param {string} iso ISO country code.
   * @returns {PhoneNumber}
   */
  private getPhoneExample(iso: string): PhoneNumber {
    return this.phoneUtil.getExampleNumberForType(iso, PhoneNumberType.MOBILE);
  }

  /**
   * @description Check is string posible phone number (google-libphonenumber).
   * @param {string} phone Current phone number as string.
   * @param {string} iso ISO country/region code.
   * @returns {PhoneNumber}
   */
  public isPossibleNumberString(phone: string, iso?: string): boolean {
    return this.phoneUtil.isPossibleNumberString(phone, iso);
  }

  /**
   * @description Converts phone number from string to PhoneNumber (google-libphonenumber).
   * @param {string} phone Current phone number as string.
   * @param {string} iso ISO country/region code.
   * @returns {PhoneNumber}
   */
  private convertedNumber(phone: string, iso?: string): PhoneNumber {
    return this.phoneUtil.parse(phone, iso);
  }
}
